import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyMetrics } from '@/components/admin/KeyMetrics';
import { UserActivityChart } from '@/components/admin/UserActivityChart';
import { SimulationCompletionChart } from '@/components/admin/SimulationCompletionChart';
import { TopPerformingAgents } from '@/components/admin/TopPerformingAgents';
import { RecentActivity } from '@/components/admin/RecentActivity';
import { SystemHealth } from '@/components/admin/SystemHealth';
import { EngagementMetrics } from '@/components/admin/EngagementMetrics';
import { ScoreDistributionChart } from '@/components/admin/ScoreDistributionChart';
import { AlertsPanel } from '@/components/admin/AlertsPanel';
import { CompanyComparisonChart } from '@/components/admin/CompanyComparisonChart';
import { SkillGapAnalysis } from '@/components/admin/SkillGapAnalysis';
import { UsageHeatmap } from '@/components/admin/UsageHeatmap';
import { QuickActionsPanel } from '@/components/admin/QuickActionsPanel';
import { DashboardExport } from '@/components/admin/DashboardExport';
import { DateRangeFilter } from '@/components/admin/DateRangeFilter';
import { GoalTrackingWidget } from '@/components/admin/GoalTrackingWidget';
import { AgentPerformanceTrends } from '@/components/admin/AgentPerformanceTrends';
import { UserRetentionChart } from '@/components/admin/UserRetentionChart';
import { LeaderboardPreview } from '@/components/admin/LeaderboardPreview';
import { differenceInDays } from 'date-fns';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { EnterpriseDashboard } from '@/pages/EnterpriseDashboard';

export function AdminDashboard() {
  const navigate = useNavigate();
  const { isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const [dateRange, setDateRange] = useState('30days');
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();

  // Older white-label tenants keep the legacy company dashboard. Enterprise
  // tenants use the hierarchy-aware dashboard below.
  useEffect(() => {
    if (!isMasterTenant && !hasEnterpriseHierarchy) {
      navigate('/company-dashboard', { replace: true });
    }
  }, [hasEnterpriseHierarchy, isMasterTenant, navigate]);

  const getDays = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return differenceInDays(customEndDate, customStartDate) + 1;
    }
    if (dateRange === '7days') return 7;
    if (dateRange === '90days') return 90;
    return 30;
  };

  if (hasEnterpriseHierarchy) return <EnterpriseDashboard />;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-4xl font-bold text-foreground">Admin Dashboard</h1>
            <div className="flex items-center gap-3">
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onCustomDateChange={(start, end) => {
                  setCustomStartDate(start);
                  setCustomEndDate(end);
                }}
              />
              <DashboardExport dateRange={dateRange} />
            </div>
          </div>
          <p className="text-lg text-muted-foreground">
            Platform overview and analytics
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Quick Actions */}
          <QuickActionsPanel />
          
          {/* Key Platform Metrics */}
          <KeyMetrics />
          
          {/* Engagement Metrics */}
          <EngagementMetrics />
          
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <UserActivityChart days={getDays()} />
            <ScoreDistributionChart />
          </div>
          
          {/* Simulation & Agent Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <SimulationCompletionChart />
            <TopPerformingAgents />
          </div>
          
          {/* Activity & Health */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <RecentActivity />
            <SystemHealth />
          </div>
          
          {/* Company Comparison & Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <CompanyComparisonChart />
            <AlertsPanel />
          </div>
          
          {/* Skill Gap & Usage Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <SkillGapAnalysis />
            <UsageHeatmap />
          </div>
          
          {/* Goal Tracking & Performance Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <GoalTrackingWidget />
            <AgentPerformanceTrends />
          </div>
          
          {/* User Retention & Leaderboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <UserRetentionChart />
            <LeaderboardPreview />
          </div>
        </div>
      </div>
    </div>
  );
}
